import React, { useState, useEffect, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SaferSavvyLogo from '../components/SaferSavvyLogo';
import { updateSEO, buildBreadcrumbSchema } from '../utils/seo';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" className="flex-shrink-0">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.33 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.67 14.62 48 24 48z"/>
  </svg>
);

const EyeOpen = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const inputCls = (hasErr?: boolean) =>
  `w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D5F9E]/30 focus:border-[#2D5F9E] transition-all ${hasErr ? 'border-red-400' : 'border-gray-300'}`;

const Signup: React.FC = () => {
  const [fullName, setFullName]                 = useState('');
  const [email, setEmail]                       = useState('');
  const [password, setPassword]                 = useState('');
  const [showPassword, setShowPassword]         = useState(false);
  const [address, setAddress]                   = useState('');
  const [phoneno, setPhoneno]                   = useState('');
  const [registrationNumber, setRegNo]          = useState('');
  const [department, setDepartment]             = useState('');
  const [agreed, setAgreed]                     = useState(false);
  const [error, setError]                       = useState('');
  const [successMessage, setSuccessMessage]     = useState('');
  const [isLoading, setIsLoading]               = useState(false);
  const [fieldErrors, setFieldErrors]           = useState<Record<string, string>>({});
  const navigate = useNavigate();

  /* ── SEO: Signup page meta ── */
  useEffect(() => {
    updateSEO({
      title: "Create Your Account — Join SaferSavvy University Transport Automation",
      description:
        "Register for a free SaferSavvy account. Access AI-powered university transport automation features — track buses, file complaints, and manage your campus commute.",
      canonical: "/signup",
      keywords: "SaferSavvy signup, create account, university transport registration, student registration",
      noIndex: true,
      jsonLd: buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Sign Up", url: "/signup" },
      ]),
    });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(''); setFieldErrors({}); setSuccessMessage('');

    if (!fullName || !email || !password || !address || !phoneno || !registrationNumber || !department) {
      setError('All fields are required'); return;
    }
    if (!agreed) { setError('Please agree to the Terms & Privacy to continue'); return; }

    const trimmedRegNo = registrationNumber.trim();
    if (!/^\d{2,4}-[A-Za-z]{2,10}-\d{1,10}$/.test(trimmedRegNo)) {
      setFieldErrors({ registrationNumber: 'Invalid format (e.g., 2022-CS-20)' }); return;
    }

    setIsLoading(true);
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}api/auth/register`, {
        full_name: fullName, email, password,
        phone_number: phoneno, address,
        registrationNumber: trimmedRegNo, department, role: 'student',
      });
      if (res.status === 201) {
        setSuccessMessage('Registration successful! Please wait for admin verification.');
        setFullName(''); setEmail(''); setPassword(''); setAddress('');
        setPhoneno(''); setRegNo(''); setDepartment(''); setAgreed(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err: any) {
      if (err.response) {
        const data = err.response.data;
        if (data.errors && Array.isArray(data.errors)) {
          const errs: Record<string, string> = {};
          data.errors.forEach((e: any) => { if (e.path && e.msg) errs[e.path] = e.msg; });
          setFieldErrors(errs);
        }
        if (data.message) setError(data.message);
        else if (!data.errors) setError('Registration failed. Please try again.');
      } else if (err.request) setError('No response from server.');
      else setError('An unexpected error occurred.');
    } finally { setIsLoading(false); }
  };

  return (
    <div className="h-screen flex overflow-hidden">

      {/* ── Left panel — form ── */}
      <div className="flex-1 bg-white flex flex-col overflow-y-auto">
        {/* Top bar */}
        <div className="flex justify-end px-8 py-3">
          <span className="text-sm text-gray-500">
            Already a member?{' '}
            <Link to="/login" className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors">
              Sign in
            </Link>
          </span>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center px-8 sm:px-12 lg:px-16 xl:px-20 py-2">
          <div className="w-full max-w-lg">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <h2 className="text-2xl font-black text-[#1D3047] mb-0.5">Create your account</h2>
              <p className="text-gray-500 text-sm mb-4">Register once. Track your bus, file complaints, and access AI transport support.</p>

              {/* Google btn */}
              <button type="button" className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-xl py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-4">
                <GoogleIcon />
                Sign up with Google
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium tracking-widest">OR WITH EMAIL</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Alerts */}
              <AnimatePresence mode="wait">
                {(error || successMessage) && (
                  <motion.div key="msg" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className={`flex items-center justify-between text-sm rounded-lg px-4 py-2.5 mb-5 ${error ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-green-50 border border-green-200 text-green-600'}`}>
                    <span>{error || successMessage}</span>
                    <button onClick={() => { setError(''); setSuccessMessage(''); }} className="ml-3 font-bold text-lg leading-none">×</button>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Row 1 */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Full name</label>
                    <input type="text" placeholder="Ayesha Kamran" value={fullName} onChange={e => setFullName(e.target.value)} className={inputCls(!!fieldErrors.full_name)} />
                    {fieldErrors.full_name && <p className="mt-1 text-xs text-red-500">{fieldErrors.full_name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" placeholder="you@university.edu" value={email} onChange={e => setEmail(e.target.value)} className={inputCls(!!fieldErrors.email)} />
                    {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Registration #</label>
                    <input type="text" placeholder="2022-CS-20" value={registrationNumber} onChange={e => setRegNo(e.target.value)} className={inputCls(!!fieldErrors.registrationNumber)} />
                    {fieldErrors.registrationNumber && <p className="mt-1 text-xs text-red-500">{fieldErrors.registrationNumber}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                    <select value={department} onChange={e => setDepartment(e.target.value)} className={inputCls(!!fieldErrors.department) + ' bg-white'}>
                      <option value="">Select Department</option>
                      <option>Computer Science</option>
                      <option>Data Science</option>
                      <option>Information Technology</option>
                      <option>Electrical Engineering</option>
                      <option>Mechanical Engineering</option>
                      <option>Civil Engineering</option>
                      <option>Public or Private Agency</option>
                      <option>Other</option>
                    </select>
                    {fieldErrors.department && <p className="mt-1 text-xs text-red-500">{fieldErrors.department}</p>}
                  </div>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                    <input type="tel" placeholder="+92 300 0000000" value={phoneno} onChange={e => setPhoneno(e.target.value)} className={inputCls(!!fieldErrors.phone_number)} />
                    {fieldErrors.phone_number && <p className="mt-1 text-xs text-red-500">{fieldErrors.phone_number}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                    <input type="text" placeholder="Your address" value={address} onChange={e => setAddress(e.target.value)} className={inputCls(!!fieldErrors.address)} />
                    {fieldErrors.address && <p className="mt-1 text-xs text-red-500">{fieldErrors.address}</p>}
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} placeholder="Create a password" value={password} onChange={e => setPassword(e.target.value)} className={inputCls(!!fieldErrors.password) + ' pr-11'} />
                    <button type="button" tabIndex={-1} onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff /> : <EyeOpen />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="mt-1 text-xs text-red-500">{fieldErrors.password}</p>}
                </div>

                {/* Terms */}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-[#1D3047]" />
                  <span className="text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms-of-service" className="font-semibold text-[#1D3047] hover:text-[#E3B02E]">Terms &amp; Privacy</Link>
                  </span>
                </label>

                <button type="submit" disabled={isLoading}
                  className="w-full bg-[#1D3047] hover:bg-[#2D5F9E] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm">
                  {isLoading ? 'Creating account...' : 'Create account'}
                </button>
              </form>

              <p className="text-center text-sm text-gray-500 mt-5">
                Already a member?{' '}
                <Link to="/login" className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors">Sign in</Link>
              </p>
            </motion.div>
          </div>
        </div>

      </div>

      {/* ── Right panel — dark branding ── */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-[42%] bg-[#1D3047] flex-col px-10 xl:px-14 py-8 overflow-hidden" style={{ backgroundImage: 'radial-gradient(ellipse at 80% 40%, rgba(45,95,158,0.45) 0%, transparent 70%)' }}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <SaferSavvyLogo size={48} />
          <span className="text-white font-bold text-xl tracking-tight">
            Safer<span style={{ color: '#E3B02E' }}>Savvy</span>
          </span>
        </Link>

        {/* Headline */}
        <div className="flex-1 flex flex-col justify-center mb-5 min-h-0">
          <h1 className="text-4xl xl:text-5xl font-black text-white leading-tight">
            Smart transport<br />
            for your <span style={{ color: '#E3B02E' }}>campus.</span>
          </h1>
        </div>

        {/* Image — constrained to match login panel */}
        <img src="/signupimg.png" alt="Campus" className="w-full rounded-2xl object-cover flex-shrink-0" style={{ height: '200px' }} />
      </div>
    </div>
  );
};

export default Signup;
